import type { AdminMe, AdminRole, Permission } from "./api";

/**
 * Droits du back-office (admin V1.51, Gestion des droits) : libellés et
 * regroupement de la grille Administration › Utilisateurs › Gestion des droits,
 * et droits du rôle courant.
 *
 * Le SERVEUR est la référence (apps/api/src/lib/permissions.ts) : il envoie les
 * droits effectifs du rôle dans `me().permissions`, réglages de la Gestion des
 * droits compris. La matrice par défaut ci-dessous n'est qu'un secours, pour une
 * API antérieure à V1.51 qui ne les envoie pas (fenêtre de déploiement).
 */

export type PermissionGroup = "Contenus" | "Billetterie et ventes" | "Reporting" | "Administration";

export const PERMISSION_GROUPS: PermissionGroup[] = ["Contenus", "Billetterie et ventes", "Reporting", "Administration"];

export interface PermissionInfo {
  label: string;
  hint: string;
  group: PermissionGroup;
}

/** Libellés de la grille, dans l'ordre d'affichage à l'intérieur de chaque groupe. */
export const PERMISSION_INFO: Record<Permission, PermissionInfo> = {
  "content.read": {
    group: "Contenus",
    label: "Voir les contenus et les applications",
    hint: "Menus Contenus, Parcours enfants et Applications. Décocher masque ces menus ; la lecture reste ouverte côté serveur (le choix de l'établissement en a besoin).",
  },
  "content.write": {
    group: "Contenus",
    label: "Créer et modifier les contenus",
    hint: "Fiches, médias, import, time codes (IA), chasses et mini-jeux.",
  },
  "content.publish": {
    group: "Contenus",
    label: "Publier et dépublier",
    hint: "Mettre en ligne ou retirer œuvres, parcours, expositions et applications.",
  },
  "billing.manage": {
    group: "Billetterie et ventes",
    label: "Billetterie : partenaires, achats, codes comptoir",
    hint: "Diagnostic d'un billet, renouvellement d'accès, achats et accès, lots de codes comptoir.",
  },
  "billing.testCodes": {
    group: "Billetterie et ventes",
    label: "Codes de test",
    hint: "Créer et supprimer des codes de test : ils ouvrent l'app sur n'importe quel téléphone, sans usage unique.",
  },
  "billing.exempt": {
    group: "Billetterie et ventes",
    label: "Exempter un billet de l'usage unique",
    hint: "Rend un vrai billet utilisable sur plusieurs téléphones.",
  },
  "billing.renewUnlimited": {
    group: "Billetterie et ventes",
    label: "Renouveler un accès sans limite",
    hint: "Au-delà de 2 renouvellements pour un même billet.",
  },
  "reporting.finance": {
    group: "Reporting",
    label: "Reporting financier : billetterie et redevance",
    hint: "Ventes, chiffre d'affaires, relevés de redevance.",
  },
  "analytics.read": {
    group: "Reporting",
    label: "Statistiques visiteurs et qualité",
    hint: "Reporting › Visiteurs et Qualité.",
  },
  "analytics.reset": {
    group: "Reporting",
    label: "Remettre les compteurs à zéro",
    hint: "Irréversible.",
  },
  "users.manage": {
    group: "Administration",
    label: "Gérer les utilisateurs",
    hint: "Comptes du back-office et Questions fréquentes. On ne donne que des rôles qui n'ont pas plus de droits que le sien.",
  },
  "users.manageSuperAdmin": {
    group: "Administration",
    label: "Gérer les comptes Super-admin",
    hint: "Créer, modifier ou désactiver un Super-admin.",
  },
  "rights.manage": {
    group: "Administration",
    label: "Gestion des droits",
    hint: "La grille rôles × droits.",
  },
  "rights.manageUser": {
    group: "Administration",
    label: "Droits par personne",
    hint: "Ajouter ou retirer un droit à une personne précise, par-dessus son rôle. Verrouillé au Super-admin : qui règle les droits d'autrui règle les siens.",
  },
  "mcp.manageTokens": {
    group: "Administration",
    label: "Jetons d'API (serveur MCP)",
    hint: "Fabriquer ses propres jetons, par lesquels le serveur MCP d'ALTO lit le back-office en son nom. Un jeton ne porte jamais plus que les droits de la personne, et jamais les droits d'administration.",
  },
  "tenant.manage": {
    group: "Administration",
    label: "Clients, revendeurs et réglages de redevance",
    hint: "Clients, revendeurs et leurs clés, catalogue, règles et clôtures de redevance.",
  },
  "demoAccess.manage": {
    group: "Administration",
    label: "Accès démo et previews",
    hint: "Créer et retirer des accès de démonstration.",
  },
  "audit.read": {
    group: "Administration",
    label: "Journal d'audit",
    hint: "Historique des actions sensibles du back-office et des refus d'accès.",
  },
  "ndp.manage": {
    group: "Administration",
    label: "Groupes Notre-Dame",
    hint: "Suivi et facturation des groupes Notre-Dame : facturation propre d'ALTO, hors musée client. Verrouillé au Super-admin — ces dossiers n'appartiennent à aucun tenant.",
  },
};

/** Matrice par défaut, miroir de DEFAULT_PERMISSION_MATRIX (API) — secours seulement. */
const DEFAULT_MATRIX: Record<Permission, AdminRole[]> = {
  "content.read": ["SUPER_ADMIN", "ADMIN", "EDITOR", "SAV", "VIEWER"],
  "content.write": ["SUPER_ADMIN", "ADMIN", "EDITOR"],
  "content.publish": ["SUPER_ADMIN", "ADMIN"],
  "billing.manage": ["SUPER_ADMIN", "ADMIN", "SAV", "COMMERCIAL"],
  "billing.testCodes": ["SUPER_ADMIN", "ADMIN", "SAV"],
  "billing.exempt": ["SUPER_ADMIN", "ADMIN"],
  "billing.renewUnlimited": ["SUPER_ADMIN", "ADMIN"],
  "users.manage": ["SUPER_ADMIN", "ADMIN"],
  "users.manageSuperAdmin": ["SUPER_ADMIN"],
  "rights.manage": ["SUPER_ADMIN"],
  "rights.manageUser": ["SUPER_ADMIN"],
  "mcp.manageTokens": ["SUPER_ADMIN"],
  "tenant.manage": ["SUPER_ADMIN"],
  "demoAccess.manage": ["SUPER_ADMIN", "ADMIN", "COMMERCIAL"],
  "analytics.read": ["SUPER_ADMIN", "ADMIN", "EDITOR", "SAV", "VIEWER", "COMMERCIAL"],
  "analytics.reset": ["SUPER_ADMIN"],
  "reporting.finance": ["SUPER_ADMIN", "ADMIN", "COMMERCIAL"],
  "audit.read": ["SUPER_ADMIN", "ADMIN", "COMMERCIAL"],
  "ndp.manage": ["SUPER_ADMIN"],
};

/**
 * Droits du compte courant : ceux envoyés par le serveur ; à défaut (API
 * antérieure à V1.51), ceux du rôle par défaut. Vide tant que `me` charge.
 */
export function permissionsOfMe(me: Pick<AdminMe, "role" | "permissions"> | undefined): ReadonlySet<Permission> {
  if (!me) return new Set();
  if (me.permissions) return new Set(me.permissions);
  return new Set((Object.keys(DEFAULT_MATRIX) as Permission[]).filter((p) => DEFAULT_MATRIX[p].includes(me.role)));
}
